import { inject, Injectable, resource, computed } from "@angular/core";
import { IframeMessagesService } from "../iframe-messages-service/iframe-messages.service";
import { ResponseErrorHandlerService } from "../response-error-handler-service/response-error-handler.service";
import type {
    IJobRoleStatusAssociation,
    IJobRoleStatusAssociationResponse,
    IJobType,
    IJobTypesResponse,
} from "../interfaces/jobs-types-response.interface";
import { DashboardsService } from "@shared/dashboards-service";

@Injectable({ providedIn: "root" })
export class JobsAndBillingJobTypesService {
    private readonly iframeMessagesService = inject(IframeMessagesService);
    private readonly responseErrorHandlerService = inject(ResponseErrorHandlerService);
    private readonly dashboardsService = inject(DashboardsService);
    private _associationsPromise: Promise<IJobRoleStatusAssociation[]> | null = null;

    private jobTypesPromise: Promise<IJobType[]> | null = null;

    private readonly jobsTypesResource = resource<IJobType[], boolean>({
        defaultValue: [],
        params: () => this.dashboardsService.currentDashboardIsJobsBilling(),
        loader: ({ params: isJobsBilling }: { params: boolean }) => {
            if (!isJobsBilling) return Promise.resolve([]);
            return this.getJobTypes();
        },
    });

    public readonly jobTypes = computed(() => new Map<number, IJobType>(this.jobsTypesResource.value().map((jt) => [jt.id, jt])));

    private readonly jobsRoleStatusAssociationResource = resource<IJobRoleStatusAssociation[], boolean>({
        defaultValue: [],
        params: () => this.dashboardsService.currentDashboardIsJobsBilling(),
        loader: ({ params: isJobsBilling }: { params: boolean }) => {
            if (!isJobsBilling) return Promise.resolve([]);
            return this.getJobRoleStatusAssociation();
        },
    });

    public readonly jobRoleStatusAssociation = computed(() => this.jobsRoleStatusAssociationResource.value());

    // Lets consumers show a localized loading state (e.g. on the Job Status dropdown) instead of blocking
    // unrelated UI while this specific, occasionally-slow endpoint is still in flight.
    public readonly isRoleStatusAssociationLoading = computed(() => this.jobsRoleStatusAssociationResource.isLoading());


    public readonly roleStatusAssociationByJobType = computed<Map<number, Set<string>>>(() => {
        const associations = this.jobRoleStatusAssociation();
        const map = new Map<number, Set<string>>();
        for (const assoc of associations) {
            let set = map.get(assoc.jobTypeId);
            if (!set) {
                set = new Set<string>();
                map.set(assoc.jobTypeId, set);
            }
            set.add(assoc.jobStatusId);
        }
        return map;
    });

    public async getJobTypes(): Promise<IJobType[]> {
        if (!this.jobTypesPromise) this.jobTypesPromise = this.loadJobTypes();
        return this.jobTypesPromise;
    }

    private async loadJobTypes(): Promise<IJobType[]> {
        try {
            const result = await this.iframeMessagesService.postMessageResp("getJobsAndBillingTypes", null, "gotJobsTypes");
            if (result.status === "success") {
                return (result.response as IJobTypesResponse)?.data?.jobTypes ?? [];
            }
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
        return [];
    }

    public async getJobRoleStatusAssociation(): Promise<IJobRoleStatusAssociation[]> {
        if (!this._associationsPromise) this._associationsPromise = this.loadJobRoleStatusAssociation();
        const associations = await this._associationsPromise;
        this.jobsRoleStatusAssociationResource.set(associations);
        return associations;
    }

    public refresh(): void {
        this._associationsPromise = null;
        this.jobsRoleStatusAssociationResource.reload();
    }

    private async loadJobRoleStatusAssociation(): Promise<IJobRoleStatusAssociation[]> {
        try {
            const result = await this.iframeMessagesService.postMessageResp(
                "getJobsAndBillingRoleStatusAssociations",
                null,
                "gotJobRoleStatusAssociations",
            );
            if (result.status === "success") {
                return (result.response as IJobRoleStatusAssociationResponse)?.data?.associations ?? [];
            }
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        } catch {
            this.responseErrorHandlerService.showPopup({
                typeLayout: "ERROR",
                messageCode: "defaultError",
            });
        }
        return [];
    }
}
